import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isJustificationRequired } from '../utils/accessRefusal';
import { agreementKeys } from './agreementKeys';

interface CacheEvent {
    type: string;
    action?: { type: string; error?: unknown };
}

const failureOf = (event: CacheEvent): unknown =>
    event.type === 'updated' && (event.action?.type === 'error' || event.action?.type === 'failed')
        ? event.action.error
        : undefined;

/** Re-reads the access state when any request for this Agreement is refused for want of a justification. */
export function useAccessRefusalWatcher(agreementId: string | undefined) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!agreementId) return;

        const accessKey = agreementKeys.access(agreementId);
        const onCacheEvent = (event: CacheEvent) => {
            if (!isJustificationRequired(failureOf(event), agreementId)) return;
            if (queryClient.isFetching({ queryKey: accessKey }) > 0) return;
            void queryClient.invalidateQueries({ queryKey: accessKey });
        };

        const unsubscribeQueries = queryClient.getQueryCache().subscribe(onCacheEvent);
        const unsubscribeMutations = queryClient.getMutationCache().subscribe(onCacheEvent);

        return () => {
            unsubscribeQueries();
            unsubscribeMutations();
        };
    }, [agreementId, queryClient]);
}
